import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FlaskConical, CircleCheck, Check, Rocket, ClipboardList, ChevronUp, ChevronDown,
  Hourglass, Link2, Folder, Store, TrendingUp, Calendar,
} from 'lucide-react';
import { apiClient } from '../packages/api-client';

// ─── Types ────────────────────────────────────────────────────────────────────

interface BetaMetric {
  label: string;
  value: string | number;
  status: 'pass' | 'warn' | 'fail';
  detail?: string;
  action?: { label: string; href: string };
}

interface BetaFeature {
  name: string;
  phase: string;
  complete: boolean;
  description: string;
}

// ─── Static data ─────────────────────────────────────────────────────────────

const FEATURES: BetaFeature[] = [
  // Core PM
  { name: 'Project management', phase: 'Core', complete: true, description: 'Projects, tasks, kanban, milestones' },
  { name: 'Task dependencies & cycle detection', phase: 'Core', complete: true, description: 'Finish-to-start deps with DFS cycle guard' },
  { name: 'Gantt / timeline view', phase: 'Core', complete: true, description: 'DHTMLX Gantt with drag-and-drop' },
  { name: 'Team workload & capacity', phase: 'Core', complete: true, description: 'Per-user task load view' },
  { name: 'Role-based access control', phase: 'Core', complete: true, description: 'Casbin RBAC with tenant isolation' },
  { name: 'Audit log', phase: 'Core', complete: true, description: 'Append-only audit trail' },
  { name: 'Time tracking', phase: 'Core', complete: true, description: 'Per-task time log with summaries' },
  { name: 'Recurring tasks', phase: 'Core', complete: true, description: 'Daily/weekly/monthly/yearly rules' },
  { name: 'Saved filters & views', phase: 'Core', complete: true, description: 'Persistent custom filter sets' },
  { name: 'Bulk actions', phase: 'Core', complete: true, description: 'Multi-select status/assignee/date update' },
  // Phase 7A
  { name: 'AI Plan Generation', phase: '7A', complete: true, description: 'NL prompt → tasks + phases + subtasks' },
  { name: 'Meeting Notes → Tasks', phase: '7A', complete: true, description: 'Extract action items from meeting notes' },
  { name: 'Ask the Project (AI)', phase: '7A', complete: true, description: 'Freeform Q&A over live project data' },
  { name: 'Daily AI Briefing', phase: '7A', complete: true, description: 'Due/overdue/blocked + suggestions' },
  { name: 'Dependency Impact Analysis', phase: '7A', complete: true, description: 'Cascade slip analysis with mitigation options' },
  { name: 'AI Status Reports', phase: '7A', complete: true, description: 'AI narrative + risks + .md export' },
  { name: 'Idea → Project (modal)', phase: '7A', complete: true, description: 'One-click project + task generation' },
  // Phase 7B
  { name: 'Event sourcing (Time Machine)', phase: '7B', complete: true, description: 'Append-only event log with state replay' },
  { name: 'Time Machine UI', phase: '7B', complete: true, description: 'Activity bars + datetime slider + snapshot Kanban' },
  { name: 'Template Marketplace', phase: '7B', complete: true, description: '10 built-in templates + AI task gen' },
];

const GO_NO_GO: { label: string; key: string }[] = [
  { label: 'All core features complete', key: 'coreFeatures' },
  { label: 'All AI Co-Pilot features complete', key: 'aiFeatures' },
  { label: 'DB migrations applied (000027, 000028)', key: 'migrations' },
  { label: 'Backend builds without errors', key: 'build' },
  { label: 'TypeScript compiles without errors', key: 'tsc' },
  { label: 'CI pipeline passing', key: 'ci' },
  { label: 'UAT guide prepared', key: 'uat' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function BetaReadiness() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [checklist, setChecklist] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem('betaChecklist') || '{}');
    } catch { return {}; }
  });

  const saveCheck = (key: string, val: boolean) => {
    const next = { ...checklist, [key]: val };
    setChecklist(next);
    localStorage.setItem('betaChecklist', JSON.stringify(next));
  };

  const totalFeatures = FEATURES.length;
  const doneFeatures = FEATURES.filter(f => f.complete).length;
  const pct = Math.round((doneFeatures / totalFeatures) * 100);

  const checksPassed = GO_NO_GO.filter(g => checklist[g.key]).length;
  const allGreen = checksPassed === GO_NO_GO.length;

  // Group features by phase
  const byPhase: Record<string, BetaFeature[]> = {};
  FEATURES.forEach(f => {
    (byPhase[f.phase] = byPhase[f.phase] ?? []).push(f);
  });

  const scoreColor = pct === 100 ? 'var(--color-success)' : pct >= 80 ? 'var(--color-warning)' : 'var(--color-danger)';

  return (
    <div className="beta-readiness">
      {/* Hero banner */}
      <div className="beta-hero">
        <div className="beta-hero-left">
          <div className="beta-hero-icon"><FlaskConical size={28} aria-hidden="true" /></div>
          <div>
            <h2 className="beta-hero-title">Beta Readiness Dashboard</h2>
            <p className="beta-hero-sub">KaziAtlas v1.0 — AI-first project management</p>
          </div>
        </div>
        <div className="beta-hero-score" style={{ color: scoreColor }}>
          <div className="beta-score-num">{pct}%</div>
          <div className="beta-score-label">Features Complete</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="beta-progress-bar">
        <div className="beta-progress-fill" style={{ width: `${pct}%`, background: scoreColor }} />
      </div>

      {/* Stats row */}
      <div className="beta-stats">
        <div className="beta-stat">
          <div className="beta-stat-num">{doneFeatures}</div>
          <div className="beta-stat-label">Features Shipped</div>
        </div>
        <div className="beta-stat">
          <div className="beta-stat-num">{totalFeatures - doneFeatures}</div>
          <div className="beta-stat-label">Remaining</div>
        </div>
        <div className="beta-stat">
          <div className="beta-stat-num">{Object.keys(byPhase).length}</div>
          <div className="beta-stat-label">Phases</div>
        </div>
        <div className={`beta-stat ${allGreen ? 'green' : 'warn'}`}>
          <div className="beta-stat-num">{checksPassed}/{GO_NO_GO.length}</div>
          <div className="beta-stat-label">Go / No-Go Checks</div>
        </div>
      </div>

      {/* Go / No-Go checklist */}
      <div className="beta-section">
        <div className="beta-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><CircleCheck size={18} aria-hidden="true" /> Go / No-Go Checklist</div>
        <div className="beta-checklist">
          {GO_NO_GO.map(item => (
            <label key={item.key} className={`beta-check-item ${checklist[item.key] ? 'checked' : ''}`}>
              <input
                type="checkbox"
                checked={!!checklist[item.key]}
                onChange={e => saveCheck(item.key, e.target.checked)}
              />
              <span>{item.label}</span>
              {checklist[item.key] && <span className="beta-check-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={12} aria-hidden="true" /> Go</span>}
            </label>
          ))}
        </div>
        {allGreen && (
          <div className="beta-launch-banner" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Rocket size={18} aria-hidden="true" /> <span>All checks passed — <strong>you are GO for beta launch!</strong></span>
          </div>
        )}
      </div>

      {/* Feature matrix */}
      <div className="beta-section">
        <div
          className="beta-section-title beta-toggle"
          onClick={() => setExpanded(e => !e)}
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <ClipboardList size={18} aria-hidden="true" /> Feature Matrix {expanded ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
        </div>
        {expanded && (
          <div className="beta-matrix">
            {Object.entries(byPhase).map(([phase, features]) => (
              <div key={phase} className="beta-phase-group">
                <div className="beta-phase-label">
                  Phase {phase} — {features.filter(f => f.complete).length}/{features.length} done
                </div>
                {features.map(f => (
                  <div key={f.name} className={`beta-feature-row ${f.complete ? 'done' : 'pending'}`}>
                    <span className="beta-feature-status" style={{ display: 'inline-flex', alignItems: 'center' }}>{f.complete ? <CircleCheck size={16} style={{ color: 'var(--ok)' }} aria-hidden="true" /> : <Hourglass size={16} style={{ color: 'var(--ink-muted)' }} aria-hidden="true" />}</span>
                    <span className="beta-feature-name">{f.name}</span>
                    <span className="beta-feature-desc">{f.description}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick links */}
      <div className="beta-section">
        <div className="beta-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Link2 size={18} aria-hidden="true" /> Quick Links</div>
        <div className="beta-links">
          <a href="/projects" className="btn btn-ghost btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Folder size={14} aria-hidden="true" /> Projects</a>
          <a href="/marketplace" className="btn btn-ghost btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Store size={14} aria-hidden="true" /> Templates</a>
          <a href="/workload" className="btn btn-ghost btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><TrendingUp size={14} aria-hidden="true" /> Workload</a>
          <a href="/timeline" className="btn btn-ghost btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Calendar size={14} aria-hidden="true" /> Timeline</a>
          <a href="/admin/audit-logs" className="btn btn-ghost btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><ClipboardList size={14} aria-hidden="true" /> Audit Logs</a>
        </div>
      </div>
    </div>
  );
}
